import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type GetForecastResult,
  type ListForecastDealsResult,
} from '@whinerp/client';
import {
  Button,
  EmptyState,
  ErrorState,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { formatCount, formatMoney, todayIn } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { ForecastSubmitPanel } from './forecast-submit-panel.js';
import { ForecastAccuracyPanel } from './forecast-accuracy-panel.js';
import { CapacityPanel } from './capacity-panel.js';

/**
 * SC-15 Forecast.
 *
 * Two numbers sit on every row and they are never added together. The weighted
 * figure is arithmetic over stage probabilities - nobody argues with it and
 * nobody believes it. The committed figure is a person saying this one closes.
 * A forecast call exists because those two disagree, so the screen has to show
 * the disagreement rather than resolve it.
 *
 * The categories are DISJOINT and the cumulative figure says what it contains.
 * "Best case" that silently includes commit is a column two people read
 * differently in the same meeting.
 *
 * And a committed number nobody can open is a number nobody can argue with, so
 * every cell expands into the deals behind it, each showing the whole deal
 * beside the share credited here - because the first question asked of a split
 * is "of what?".
 */

type Forecast = GetForecastResult;
type Row = Forecast['rows'][number];
type Cell = Row['cells'][number];

const THIS_YEAR = new Date().getUTCFullYear();
const YEARS = [THIS_YEAR + 1, THIS_YEAR, THIS_YEAR - 1];
const CATEGORIES = ['commit', 'best-case', 'pipeline', 'omitted'] as const;

function ForecastScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const [fiscalYear, setFiscalYear] = useState(THIS_YEAR);
  const [periodKind, setPeriodKind] = useState<'month' | 'quarter' | 'year'>('quarter');
  const [subjectKind, setSubjectKind] = useState<'user' | 'team'>('user');
  const [opened, setOpened] = useState<string | null>(null);

  /*
   * Opening the forecast is what makes the weekly record exist.
   *
   * There is no scheduler in this product. The capture is idempotent for the
   * week - the first one wins and later ones write nothing - so running it on
   * every mount is safe, and not running it means the accuracy report has
   * nothing to read. Once per mount, not once per render.
   */
  const queryClient = useQueryClient();
  const captured = useRef(false);
  const capture = useMutation({
    mutationFn: (input: { fiscalYear: number; periodKind: 'month' | 'quarter' | 'year' }) =>
      api.captureForecastSnapshot({ body: input }),
    /*
     * The accuracy panel reads what this just wrote.
     *
     * Without telling it, the panel's query can resolve before the capture
     * lands, cache an empty answer and never ask again - so the first person
     * to open the forecast in a week sees "nothing captured for this period"
     * over a record that now exists, until they reload. Found as an
     * intermittent test failure, which is what a race looks like from outside.
     */
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['forecast-accuracy'] }),
  });
  const takeSnapshot = capture.mutate;
  useEffect(() => {
    if (captured.current) return;
    captured.current = true;
    takeSnapshot({ fiscalYear, periodKind });
    /*
     * A ref rather than a query, because this WRITES - React's development
     * double-mount would otherwise fire it twice. Harmless here, since a
     * second capture in a week writes nothing, but a write fired twice by a
     * rendering strategy is a habit that stops being harmless when copied.
     *
     * Failure is deliberately silent. Somebody opening the forecast came to
     * read it; a banner about a background capture would sit on top of the
     * work, and the forecast is unaffected by it.
     */
  }, [takeSnapshot, fiscalYear, periodKind]);

  const forecast = useQuery<Forecast, ApiError>({
    queryKey: ['forecast', fiscalYear, periodKind, subjectKind],
    queryFn: () => api.getForecast({ query: { fiscalYear, periodKind, subjectKind } }),
    /*
     * The last answer stays on screen while the next one loads.
     *
     * Without it, changing the period sends the whole screen to its loading
     * state - which unmounts the selector that was just used, mid-use. The
     * control that drives a refetch has to survive being driven.
     */
    placeholderData: keepPreviousData,
  });

  if (forecast.isPending) return <ForecastLoading />;
  if (forecast.isError) {
    return <ForecastError error={forecast.error} retry={() => void forecast.refetch()} />;
  }

  const data = forecast.data;
  const money = (amount: string): string => formatMoney(amount, format, { currency: data.currency });
  const periodLabel = (period: Forecast['periods'][number]): string =>
    period.index === null
      ? t('target.period.custom', { values: { start: period.start, end: period.end } })
      : t(`target.period.${period.kind}` as 'target.period.quarter', {
          values: { index: formatCount(period.index, format) },
        });

  const today = todayIn(format);
  const accuracyPeriod =
    data.periods.find((period) => today >= period.start && today <= period.end) ??
    data.periods[data.periods.length - 1] ??
    null;

  const anyTarget = data.rows.some((row) => row.cells.some((cell) => cell.target !== null));
  /*
   * Closed business counts as something to show.
   *
   * A finished year has no open deals by definition, so a check over the
   * category totals alone sent every past year to the empty state - and a past
   * year is exactly what somebody opens this screen for, to read the accuracy
   * of a forecast whose period has actually ended.
   */
  const anyDeal = data.rows.some((row) =>
    row.cells.some(
      (cell) =>
        Number(cell.closed) !== 0 || CATEGORIES.some((c) => Number(cell.byCategory[c].amount) !== 0),
    ),
  );
  if (!anyTarget && !anyDeal && data.undated.deals === 0) return <ForecastEmpty />;

  return (
    <div data-component="ForecastGrid" className="flex w-full flex-col gap-5">
      <PageHeading
        kicker={t('object.deal.plural')}
        lede={t('forecast.body')}
        actions={
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-dense font-medium text-ink">
              {t('target.year')}
              <select
                data-forecast-year
                className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={String(fiscalYear)}
                onChange={(event) => setFiscalYear(Number(event.target.value))}
              >
                {YEARS.map((year) => (
                  <option key={year} value={String(year)}>
                    {String(year)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-dense font-medium text-ink">
              {t('target.periodKind')}
              <select
                data-forecast-period
                className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={periodKind}
                onChange={(event) =>
                  setPeriodKind(
                    event.target.value === 'month'
                      ? 'month'
                      : event.target.value === 'year'
                        ? 'year'
                        : 'quarter',
                  )
                }
              >
                <option value="month">{t('target.periodKind.month')}</option>
                <option value="quarter">{t('target.periodKind.quarter')}</option>
                <option value="year">{t('target.periodKind.year')}</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-dense font-medium text-ink">
              {t('target.by')}
              <select
                data-forecast-subject
                className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={subjectKind}
                onChange={(event) => setSubjectKind(event.target.value === 'team' ? 'team' : 'user')}
              >
                <option value="user">{t('target.subject.user')}</option>
                <option value="team">{t('target.subject.team')}</option>
              </select>
            </label>
          </div>
        }
      >
        {t('forecast.title')}
      </PageHeading>

      {/* Each names a scope these figures do not cover, and each is drawn only
          when it applies. */}
      {data.undated.deals > 0 ? (
        <p data-forecast-undated className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text">
          {t('forecast.undated', {
            values: {
              amount: money(data.undated.amount),
              count: formatCount(data.undated.deals, format),
            },
          })}
        </p>
      ) : null}
      {data.unattributed.deals > 0 ? (
        <p
          data-forecast-unattributed
          className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text"
        >
          {t('forecast.unattributed', {
            values: {
              amount: money(data.unattributed.amount),
              count: formatCount(data.unattributed.deals, format),
            },
          })}
        </p>
      ) : null}
      {data.unconvertible.length > 0 ? (
        <p
          data-forecast-unconvertible
          className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text"
        >
          {t('forecast.unconvertible', {
            values: { currencies: [...new Set(data.unconvertible.map((gap) => gap.from))].join(', ') },
          })}
        </p>
      ) : null}
      {!anyTarget ? (
        <p data-forecast-no-targets className="rounded-control bg-sunken px-3 py-2 text-dense text-ink-meta">
          {t('forecast.noTargets')}{' '}
          <a href="/setup#targets" className="text-bronze-text underline">
            {t('forecast.setTargets')}
          </a>
        </p>
      ) : null}

      {data.periods.map((period) => (
        <Panel key={period.start} data-forecast-period-panel={period.start}>
          <div className="flex flex-col gap-3">
            <SectionLabel>{periodLabel(period)}</SectionLabel>
            <div data-scroll-container className="overflow-x-auto">
              <table className="w-full min-w-block-xl border-collapse text-body">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="border-b border-b-rule py-2 text-start text-micro uppercase tracking-label text-ink-label"
                    >
                      {subjectKind === 'user' ? t('target.subject.user') : t('target.subject.team')}
                    </th>
                    {CATEGORIES.map((category) => (
                      <th
                        key={category}
                        scope="col"
                        className="border-b border-b-rule py-2 text-end text-micro uppercase tracking-label text-ink-label"
                      >
                        {t(`forecast.category.${category}` as 'forecast.category.commit')}
                      </th>
                    ))}
                    <th
                      scope="col"
                      title={t('forecast.weighted.hint')}
                      className="border-b border-b-rule py-2 text-end text-micro uppercase tracking-label text-ink-label"
                    >
                      {t('forecast.weighted')}
                    </th>
                    <th
                      scope="col"
                      className="border-b border-b-rule py-2 text-end text-micro uppercase tracking-label text-ink-label"
                    >
                      {t('forecast.closed')}
                    </th>
                    <th
                      scope="col"
                      className="border-b border-b-rule py-2 text-end text-micro uppercase tracking-label text-ink-label"
                    >
                      {t('forecast.target')}
                    </th>
                    <th scope="col" className="border-b border-b-rule py-2">
                      {/* Never blank. A screen reader announces every column by
                          its header, and an empty one leaves the cell under it
                          with no name at all. */}
                      <span className="sr-only">{t('forecast.open')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => {
                    const cell = row.cells.find((entry) => entry.periodStart === period.start);
                    if (!cell) return null;
                    const key = `${row.subject.id}|${period.start}`;
                    return (
                      <ForecastRowCells
                        key={row.subject.id}
                        row={row}
                        cell={cell}
                        cellKey={key}
                        money={money}
                        opened={opened === key}
                        onToggle={() => setOpened(opened === key ? null : key)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Panel>
      ))}

      {/* The submission sits under the grid for each period, because the
          number somebody commits to is read off the grid above it. */}
      {data.periods.map((period) => (
        <ForecastSubmitPanel
          key={`submit-${period.start}`}
          periodKind={data.periodKind}
          periodStart={period.start}
          periodEnd={period.end}
          periodLabel={periodLabel(period)}
          currency={data.currency}
        />
      ))}

      {/* How wrong the record has been.
          
          For the period TODAY falls in, not the first of the year. A review
          asks about the period it is in, and opening a forecast in September
          onto January's accuracy would answer a question nobody had. The last
          period is the fallback, for a year already over. */}
      {accuracyPeriod ? (
        <ForecastAccuracyPanel
          periodStart={accuracyPeriod.start}
          periodEnd={accuracyPeriod.end}
          periodLabel={periodLabel(accuracyPeriod)}
        />
      ) : null}

      {/* Coverage and headcount for the same period (G-148). */}
      {accuracyPeriod ? (
        <CapacityPanel
          periodStart={accuracyPeriod.start}
          periodEnd={accuracyPeriod.end}
          periodLabel={periodLabel(accuracyPeriod)}
        />
      ) : null}

      <p className="text-meta text-ink-meta">{t('forecast.scope')}</p>
    </div>
  );
}

function ForecastRowCells({
  row,
  cell,
  cellKey,
  money,
  opened,
  onToggle,
}: {
  row: Row;
  cell: Cell;
  cellKey: string;
  money: (amount: string) => string;
  opened: boolean;
  onToggle: () => void;
}): JSX.Element {
  const t = useT();

  /*
   * The variance arrives computed. It is a subtraction of two money values and
   * a decision about what to compare - what has closed plus what somebody is
   * committing to, never the weighted figure - and neither of those belongs in
   * a browser doing float arithmetic on a number somebody is measured by.
   */
  const short = cell.variance !== null && cell.variance.startsWith('-');

  return (
    <>
      <tr data-forecast-row={cellKey}>
        <th scope="row" className="border-b border-b-rule py-2 text-start font-medium text-ink">
          {row.subject.name}
        </th>
        {CATEGORIES.map((category) => (
          <td
            key={category}
            data-forecast-category={`${cellKey}|${category}`}
            className="border-b border-b-rule py-2 text-end"
          >
            <Num className={category === 'omitted' ? 'text-ink-meta' : 'text-ink'}>
              {money(cell.byCategory[category].amount)}
            </Num>
          </td>
        ))}
        <td data-forecast-weighted={cellKey} className="border-b border-b-rule py-2 text-end">
          <Num className="text-ink-meta">{money(cell.weighted)}</Num>
        </td>
        <td data-forecast-closed={cellKey} className="border-b border-b-rule py-2 text-end">
          <Num className="text-ink">{money(cell.closed)}</Num>
        </td>
        <td data-forecast-target={cellKey} className="border-b border-b-rule py-2 text-end">
          <div className="flex flex-col items-end">
            <Num className="text-ink">{cell.target === null ? '—' : money(cell.target)}</Num>
            <span data-forecast-variance={cellKey} className="text-meta text-ink-meta">
              {cell.variance === null
                ? t('forecast.variance.none')
                : short
                  ? t('forecast.variance.under', { values: { amount: money(cell.variance.slice(1)) } })
                  : t('forecast.variance.over', { values: { amount: money(cell.variance) } })}
            </span>
          </div>
        </td>
        <td className="border-b border-b-rule py-2 text-end">
          <Button
            intent="ghost"
            size="small"
            data-forecast-open={cellKey}
            aria-expanded={opened}
            onClick={onToggle}
          >
            {opened ? t('forecast.close') : t('forecast.open')}
          </Button>
        </td>
      </tr>
      {opened ? (
        <tr data-forecast-drilldown={cellKey}>
          <td colSpan={CATEGORIES.length + 4} className="border-b border-b-rule py-2">
            <ForecastDeals subject={row.subject} periodStart={cell.periodStart} periodEnd={cell.periodEnd} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ForecastDeals({
  subject,
  periodStart,
  periodEnd,
}: {
  subject: Row['subject'];
  periodStart: string;
  periodEnd: string;
}): JSX.Element {
  const t = useT();
  const format = useFormat();
  const deals = useQuery<ListForecastDealsResult, ApiError>({
    queryKey: ['forecast-deals', subject.kind, subject.id, periodStart, periodEnd],
    queryFn: () =>
      api.listForecastDeals({
        query: { subjectKind: subject.kind, subjectId: subject.id, periodStart, periodEnd },
      }),
  });

  if (deals.isPending) return <Skeleton className="h-block-xs w-full" />;
  if (deals.isError) {
    return <ErrorState title={t('state.error')} body={deals.error.message} onRetry={() => void deals.refetch()} />;
  }
  if (deals.data.deals.length === 0) {
    return <p className="text-meta text-ink-meta">{t('forecast.noDeals')}</p>;
  }

  return (
    <div className="flex flex-col gap-1">
      <ul className="flex flex-col">
        {deals.data.deals.map((deal) => (
          <li
            key={deal.dealId}
            data-forecast-deal={deal.dealId}
            className="flex flex-wrap items-baseline gap-3 border-t border-t-rule py-1 first:border-t-0"
          >
            <a href={`/deals/${deal.dealId}`} className="min-w-0 flex-1 truncate text-body text-ink hover:text-bronze-text">
              {deal.name}
            </a>
            <span className="text-meta text-ink-meta">
              {t(`forecast.category.${deal.category}` as 'forecast.category.commit')}
            </span>
            <span className="text-meta text-ink-meta">{deal.stage}</span>
            <Num data-forecast-deal-credited={deal.dealId} className="text-body text-ink">
              {formatMoney(deal.credited, format, { currency: deals.data.currency })}
            </Num>
            {/* The whole deal beside the share, because the first question
                asked of a split is "of what?". */}
            <span data-forecast-deal-share={deal.dealId} className="text-meta text-ink-meta">
              {deal.splitPercent === 100
                ? t('forecast.deal.whole')
                : t('forecast.deal.split', {
                    values: {
                      percent: formatCount(deal.splitPercent, format),
                      amount: formatMoney(deal.amount, format, { currency: deal.currency }),
                    },
                  })}
            </span>
          </li>
        ))}
      </ul>
      {deals.data.unconvertible > 0 ? (
        <p data-forecast-drilldown-short className="text-meta text-warning-text">
          {t('forecast.drilldown.short', {
            values: { count: formatCount(deals.data.unconvertible, format) },
          })}
        </p>
      ) : null}
    </div>
  );
}

function ForecastEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="TrendUp"
      title={t('forecast.empty.title')}
      body={t('forecast.empty.body')}
      primary={{ label: t('forecast.setTargets'), href: '/setup#targets', icon: 'Target' }}
    />
  );
}

function ForecastLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-5" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      <Skeleton className="h-block-lg w-full" />
    </div>
  );
}

function ForecastError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The forecast could not be loaded"
      body={error instanceof Error ? error.message : 'The forecast did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-15',
  route: '/forecast',
  surface: 'page',
  title: 'Forecast',
  // F-102 as well as F-052: the accuracy panel lives here, because the period
  // somebody reads a forecast for is the period they ask how wrong it was.
  // A screen that renders a capability and does not declare it is a capability
  // nothing can find.
  features: ['F-052', 'F-102'],
  entitlement: 'F-052',
  reachedBy: [{ kind: 'nav', item: 'forecast' }],
  // No `components`: everything 04_Screens names for this screen is either a
  // state of its own (the empty state prompts for targets) or the grid itself,
  // which is the element below.
  components: [],
  elements: ['ForecastGrid'],
  states: {
    empty: ForecastEmpty,
    loading: ForecastLoading,
    error: ForecastError,
  },
  component: ForecastScreen,
});

export default screen;
